// Branchement React de `restauration-defilement.ts` — monté UNE fois, dans la
// coquille (`App.tsx`), donc valable pour tous les écrans de tous les modules.

import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import {
  DELAI_MAX_RESTAURATION_MS,
  appliquerInstantane,
  cleEcran,
  ecrireInstantane,
  estTermine,
  lireInstantane,
  releverConteneur,
  type InstantaneDefilement,
  type PositionConteneur,
} from './restauration-defilement';

/** Gestes qui prouvent que l'utilisatrice a repris la main : on cesse de la « ramener ». */
const GESTES_UTILISATRICE = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const;

/** Durée pendant laquelle la position restaurée doit tenir avant qu'on lâche. */
const STABILITE_MS = 1_500;

export function useRestaurationDefilement(): void {
  const location = useLocation();
  const navigationType = useNavigationType();
  const cle = cleEcran(location.pathname, location.search);

  const cleRef = useRef(cle);
  // Instantané en cours de constitution pour l'écran affiché.
  const courantRef = useRef<{ fenetre: number; conteneurs: Record<string, PositionConteneur> }>({
    fenetre: 0,
    conteneurs: {},
  });
  // Tant qu'une restauration est en cours, les défilements observés sont les
  // NÔTRES (ou des troncatures pendant le chargement) : ne pas les noter.
  const restaurationRef = useRef<InstantaneDefilement | null>(null);

  // L'app gère seule : sinon le navigateur repositionne la fenêtre au F5 AVANT
  // l'arrivée des données (position tronquée) et se bat avec nous.
  useEffect(() => {
    try {
      const avant = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
      return () => {
        window.history.scrollRestoration = avant;
      };
    } catch {
      return undefined;
    }
  }, []);

  // Relevé continu (phase de capture : les événements `scroll` des
  // conteneurs ne remontent pas). Coalescé à l'image : le calcul de
  // signature ne tourne qu'une fois par frame et par conteneur.
  useEffect(() => {
    const aTraiter = new Set<EventTarget>();
    let frame = 0;
    let minuterie: ReturnType<typeof setTimeout> | undefined;
    const persister = (): void => {
      clearTimeout(minuterie);
      minuterie = undefined;
      ecrireInstantane(cleRef.current, courantRef.current);
    };
    const traiter = (): void => {
      frame = 0;
      if (restaurationRef.current) {
        aTraiter.clear();
        return;
      }
      const courant = courantRef.current;
      for (const cible of aTraiter) {
        if (cible === document || cible === document.documentElement) {
          courant.fenetre = Math.round(window.scrollY);
        } else if (cible instanceof Element && cible.isConnected) {
          const releve = releverConteneur(cible);
          if (!releve) continue;
          if (releve.position.top > 0) courant.conteneurs[releve.signature] = releve.position;
          else delete courant.conteneurs[releve.signature];
        }
      }
      aTraiter.clear();
      clearTimeout(minuterie);
      minuterie = setTimeout(persister, 200);
    };
    const surDefilement = (e: Event): void => {
      if (e.target) aTraiter.add(e.target);
      if (!frame) frame = requestAnimationFrame(traiter);
    };
    const avantDepart = (): void => {
      if (minuterie !== undefined) persister();
    };
    document.addEventListener('scroll', surDefilement, { capture: true, passive: true });
    window.addEventListener('pagehide', avantDepart);
    return () => {
      document.removeEventListener('scroll', surDefilement, { capture: true });
      window.removeEventListener('pagehide', avantDepart);
      if (frame) cancelAnimationFrame(frame);
      avantDepart();
    };
  }, []);

  // Changement d'écran — en layout effect : la clé doit basculer AVANT que le
  // navigateur ne signale la troncature de la fenêtre due au démontage de
  // l'ancien écran (sinon elle écraserait la position notée de l'ancien).
  useLayoutEffect(() => {
    const precedente = cleRef.current;
    if (precedente !== cle) ecrireInstantane(precedente, courantRef.current);
    cleRef.current = cle;

    const memorise = navigationType === 'POP' ? lireInstantane(cle) : null;
    courantRef.current = memorise
      ? { fenetre: memorise.fenetre, conteneurs: { ...memorise.conteneurs } }
      : { fenetre: 0, conteneurs: {} };

    if (!memorise || estTermine(memorise)) {
      restaurationRef.current = null;
      // Nouvel écran ouvert par un lien : on le présente depuis le haut
      // (sans quoi il héritait de la position de la page précédente).
      if (navigationType !== 'POP' && precedente.split('?')[0] !== location.pathname) window.scrollTo(0, 0);
      return undefined;
    }

    // Précédent/Suivant ou rechargement : ré-appliquer dès que possible.
    restaurationRef.current = memorise;
    const debut = Date.now();
    let frame = 0;
    const arreter = (): void => {
      restaurationRef.current = null;
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      for (const g of GESTES_UTILISATRICE) window.removeEventListener(g, arreter, true);
    };
    // « Atteint » ne suffit pas : un tableau virtualisé mesure ses lignes
    // juste après le saut et recale son défilement (cf. `PositionConteneur`).
    // On ré-applique donc la cible jusqu'à ce qu'elle TIENNE `STABILITE_MS`
    // d'affilée.
    let stableDepuis = 0;
    const tenter = (): void => {
      frame = 0;
      if (!restaurationRef.current) return;
      const maintenant = Date.now();
      if (estTermine(appliquerInstantane(memorise))) {
        if (!stableDepuis) stableDepuis = maintenant;
        if (maintenant - stableDepuis >= STABILITE_MS) {
          arreter();
          return;
        }
      } else {
        stableDepuis = 0;
      }
      if (maintenant - debut > DELAI_MAX_RESTAURATION_MS) {
        arreter();
        return;
      }
      frame = requestAnimationFrame(tenter);
    };
    for (const g of GESTES_UTILISATRICE) window.addEventListener(g, arreter, true);
    frame = requestAnimationFrame(tenter);
    return arreter;
  }, [cle, navigationType, location.pathname, location.key]);
}
